// =============================================================================
// features/policies/MonitoringBanner.tsx — Aviso permanente de linha
// corporativa monitorada (F51-S17).
//
// Vive no topo da tela `Meu WhatsApp` e no topo da caixa pessoal
// (`MeuWhatsAppInboxPage`) — SEMPRE visível, sem opção de "não mostrar de
// novo". É distinto de `SessionStatusBanner` (que só aparece quando a sessão
// está fora do ar): este aqui é permanente, tom informativo (`--info`), nunca
// alerta (`--danger`/`--warning`) — não é um problema a resolver, é a
// declaração de que a linha é corporativa.
//
// DS: tokens canônicos (docs/18-design-system.md) — --info/--info-bg,
// Geist (font-sans) para o corpo.
// =============================================================================
import * as React from 'react';
import { Link } from 'react-router-dom';

export interface MonitoringBannerProps {
  /**
   * Link para o guia completo (docs/help/guias/livechat/
   * monitoramento-linha-corporativa.mdx, publicado na Central de Ajuda).
   */
  readonly helpHref?: string;
}

const DEFAULT_HELP_HREF = '/ajuda/guias/livechat/monitoramento-linha-corporativa';

/**
 * MonitoringBanner — aviso sóbrio e permanente de que a linha é corporativa
 * e monitorada. Não depende do estado de ciência do funcionário (aparece
 * mesmo depois de aceito) — é a transparência do doc 17 §4 (princípio VI),
 * não um passo de onboarding que some.
 */
export function MonitoringBanner({
  helpHref = DEFAULT_HELP_HREF,
}: MonitoringBannerProps): React.JSX.Element {
  return (
    <div
      role="note"
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 rounded-sm"
      style={{
        background: 'var(--info-bg)',
        border: '1px solid var(--info)',
      }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className="flex-shrink-0"
        >
          <circle cx="8" cy="8" r="7" stroke="var(--info)" strokeWidth="1.5" />
          <path d="M8 7v4.2" stroke="var(--info)" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="8" cy="4.6" r="0.9" fill="var(--info)" />
        </svg>
        <p className="font-sans" style={{ fontSize: 'var(--text-sm)', color: 'var(--info)' }}>
          Esta linha de WhatsApp é corporativa. As conversas são registradas pelo sistema e podem
          ser acessadas pela gestão para supervisão do atendimento.
        </p>
      </div>
      <Link
        to={helpHref}
        className="flex-shrink-0 font-sans font-semibold underline transition-opacity duration-fast hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-azul/30 rounded-xs"
        style={{ fontSize: 'var(--text-xs)', color: 'var(--info)' }}
      >
        Saiba mais
      </Link>
    </div>
  );
}
